// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { PaginationModel, ProductCollectionModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@plumberssupply/App/Components/Notify";
import RelatedProductCart from "@plumberssupply/App/Components/SystemBuilder/RelatedProductCart";
import getSystemRelatedProducts from "@plumberssupply/App/Handlers/SystemBuilder/getSystemRelatedProducts";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import * as React from "react";
import { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    productId: string;
}

export interface SystemBuilderRelatedProductsModalStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    notify?: InjectableCss;
    container?: InjectableCss;
    itemsGridContainer?: InjectableCss;
    gridItem?: InjectableCss;
    loadMoreButton?: InjectableCss;
    modal?: ModalPresentationProps;
}
export const systemBuilderRelatedProductsModalStyles: SystemBuilderRelatedProductsModalStyles = {};

const mapStateToProps = (state: CustomAppState, productId: OwnProps) => {
    const id = state.xSystemBuilder.condenserProductId;

    return {
        modalIsOpen: state.xSystemBuilder.isRelatedProductsModalOpen,
        condenserProductId: id,
        modalRelatedProducts: state.xSystemBuilder.relatedProducts?.[id || ""]?.products,
        isTabletScreen: getXngCommon(state).isTabletScreen,
    };
};

const mapDispatchToProps = {
    getSystemRelatedProducts,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;
const styles = systemBuilderRelatedProductsModalStyles;
const SystemBuilderRelatedProductsModal: FC<Props> = ({
    modalIsOpen,
    getSystemRelatedProducts,
    condenserProductId,
    modalRelatedProducts,
    isTabletScreen,
}) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    //   const [products, setProducts] = useState<ProductModel[]>([]);
    const [pagination, setPagination] = useState<PaginationModel | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        if (condenserProductId && !modalRelatedProducts?.length) {
            getRelatedProducts(1);
        }
    }, [condenserProductId]);

    if (!condenserProductId) {
        return null;
    }

    const getRelatedProducts = (page: number) => {
        setIsLoading(true);
        // this means we clicked on load more
        const pageSize = pagination?.pageSize || undefined;

        getSystemRelatedProducts({
            parameter: {
                relationship: "Accessory",
                withPagination: true,
                page,
                productId: condenserProductId,
                pageSize,
            },
            onComplete() {
                setIsLoading(false);
                setCurrentPage(currentPage + 1);
            },
            onSuccess(result: ProductCollectionModel) {
                setPagination(result?.pagination || undefined);
            },
            onError(error: string) {
                setErrorMessage(error);
            },
        });
    };

    const modalCloseHandler = () => {
        dispatch({
            type: "xSystemBuilder/UpdateIsModalOpen",
            isModalOpen: false,
        } as any);
    };

    const loadMoreButtonHandler = () => {
        // call api for page + 1
        pagination && getRelatedProducts(pagination.page + 1);
    };

    // index where the last row of items starts - for style purposes
    const itemsPerRow = isTabletScreen ? 2 : 3;
    const totalItems = modalRelatedProducts?.length || 0;
    const lastRowStartIndex = totalItems - (totalItems % itemsPerRow || itemsPerRow);

    return (
        <>
            {!!modalRelatedProducts?.length && (
                <Modal
                    {...styles.modal}
                    headline={translate("Missing Anything?")}
                    isOpen={modalIsOpen}
                    handleClose={modalCloseHandler}
                >
                    <LoadingOverlay loading={isLoading && modalRelatedProducts?.length > 0}>
                        <StyledWrapper {...styles.container}>
                            {modalRelatedProducts && modalRelatedProducts.length > 0 ? (
                                <>
                                    <StyledWrapper {...styles.itemsGridContainer}>
                                        {modalRelatedProducts.map((product, index) => (
                                            <StyledWrapper
                                                key={product.id}
                                                {...styles.gridItem}
                                                className={index >= lastRowStartIndex ? "noBorder" : ""}
                                            >
                                                <RelatedProductCart
                                                    key={product.id}
                                                    carouselId="related-products-modal"
                                                    product={product}
                                                    showImage={true}
                                                    showBrand={true}
                                                    showTitle={true}
                                                    showPartNumbers={true}
                                                    showPrice={true}
                                                    showAddToCart={true}
                                                    showAddToList={true}
                                                />
                                            </StyledWrapper>
                                        ))}
                                    </StyledWrapper>
                                    {pagination && pagination.currentPage < pagination.numberOfPages && (
                                        <StyledWrapper {...styles.loadMoreButton}>
                                            <Button
                                                disabled={isLoading}
                                                variant="secondary"
                                                onClick={loadMoreButtonHandler}
                                            >
                                                {translate("Load More")}
                                            </Button>
                                        </StyledWrapper>
                                    )}
                                </>
                            ) : (
                                <>
                                    {isLoading && !modalRelatedProducts?.length ? (
                                        <StyledWrapper {...styles.centeringWrapper}>
                                            <LoadingSpinner />
                                        </StyledWrapper>
                                    ) : (
                                        <Notify type="warning" {...styles.notify}>
                                            {translate("No products found")}
                                        </Notify>
                                    )}
                                </>
                            )}
                        </StyledWrapper>
                    </LoadingOverlay>
                </Modal>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SystemBuilderRelatedProductsModal);
